import * as React from 'react';
import { createContext, ReactNode, useState } from 'react';
import { IMergedAnsweredItem, IQuotationAnswer } from '@/types';

interface QuotationAnswerContextProps {
  children: ReactNode;
}

type QuotationAnswerContextData = {
  quotationAnswer: IQuotationAnswer;
  setQuotationAnswer: React.Dispatch<React.SetStateAction<IQuotationAnswer>>;
};

export const QuotationAnswerContext = createContext<QuotationAnswerContextData>(
  {} as QuotationAnswerContextData
);

export function QuotationAnswerProvider({
  children,
}: QuotationAnswerContextProps) {
  const [quotationAnswer, setQuotationAnswer] = useState(
    {} as IQuotationAnswer
  );
  return (
    <QuotationAnswerContext.Provider
      value={{ quotationAnswer, setQuotationAnswer }}
    >
      {children}
    </QuotationAnswerContext.Provider>
  );
}

type SetMergedAnsweredItemsCallback<T = IMergedAnsweredItem[]> =
  | T
  | ((prevState: T) => T);

export const useQuotationAnswer = () => {
  const context = React.useContext(QuotationAnswerContext);

  if (!context) {
    throw Error('Must be inside a QuotationAnswerProvider');
  }

  const mergedAnsweredItems = context.quotationAnswer.merged_answered_items;

  const setMergedAnsweredItems = (callback: SetMergedAnsweredItemsCallback) => {
    if (typeof callback === 'function') {
      context.setQuotationAnswer(prevQuotationAnswer => {
        const newMergedAnsweredItems = callback(
          prevQuotationAnswer.merged_answered_items
        );

        return {
          ...prevQuotationAnswer,
          merged_answered_items: newMergedAnsweredItems,
          answered_items: newMergedAnsweredItems.flatMap(
            ({ answered_items }) => answered_items
          ),
        };
      });
      return;
    }

    context.setQuotationAnswer(prevQuotationAnswer => {
      return {
        ...prevQuotationAnswer,
        merged_answered_items: callback,
        answered_items: callback.flatMap(
          ({ answered_items }) => answered_items
        ),
      };
    });
  };

  return { ...context, mergedAnsweredItems, setMergedAnsweredItems };
};
